/**
 * matterResearchScope — the matter's jurisdiction and language, in the shape
 * a research session takes them.
 *
 * A session started from the matter must answer IN the matter's language and
 * retrieve FROM the matter's jurisdiction. Both facts are already on
 * `matterData` — the Context pane's Overview chips read exactly these fields —
 * but neither is stored in the form the research settings want, so the
 * translation lives here rather than inline in the pane:
 *
 *   Jurisdiction  stored as the backend's own value ("BU" for Bulgaria, a
 *                 country name, an alpha-3) → the ISO-3166 alpha-2 code that
 *                 `ResearchSource.jurisdiction` and the session's
 *                 `jurisdiction` override are both keyed by ("BG").
 *   Language      stored as a BCP-47-ish code, possibly cased or tagged
 *                 ("EN", "en-GB") → the bare lowercase code the AI-languages
 *                 dropdown lists and `output_language` stores ("en").
 *
 * Everything here answers `null`/`[]` for a matter that does not carry the
 * fact, so the caller can omit the key entirely and let the backend's own
 * resolution (session → user AI settings → platform default) stand. A missing
 * matter jurisdiction must NOT be written as `null`, which the settings
 * endpoint reads as "clear the override".
 */

import { resolveFlagCode } from '../../../utils/jurisdictionFlag';
import { ResearchSource } from '../../../store/actions/ChatAction';

/**
 * The matter's jurisdiction as a research jurisdiction code.
 *
 * Only a two-letter country answers. `resolveFlagCode` also resolves
 * subdivisions ("HAMBURG"), and those have no index of their own — scoping
 * retrieval to a code no source carries would return nothing at all, which is
 * strictly worse than the platform default.
 */
export function matterJurisdictionCode(matterData: any): string | null {
  const raw =
    matterData?.Jurisdiction ||
    matterData?.jurisdiction ||
    matterData?.fields?.Jurisdiction ||
    '';
  if (!raw) return null;
  const code = resolveFlagCode(raw);
  return code && code.length === 2 ? code : null;
}

/**
 * The matter's output language, as `output_language` stores it.
 *
 * A region tag is dropped: the matter may say "en-GB" where the AI-languages
 * list only knows "en", and answering in English is the point — the region
 * was never what the setting selected.
 */
export function matterOutputLanguage(matterData: any): string {
  const raw =
    matterData?.Language ||
    matterData?.language ||
    matterData?.fields?.Language ||
    '';
  if (typeof raw !== 'string') return '';
  const code = raw.trim().toLowerCase().split(/[-_]/)[0];
  return /^[a-z]{2,3}$/.test(code) ? code : '';
}

/**
 * Every catalogue index in one jurisdiction.
 *
 * The session's source scope is a SET OF INDEX NAMES (see `sources.ts`), not a
 * country — so "this matter's jurisdiction" has to be expanded against the
 * catalogue. All of the jurisdiction's indexes are taken deliberately: the
 * matter names a country, not a preference between its case law and its
 * legislation, and narrowing further here would be inventing a choice the
 * user never made.
 *
 * User datasources are NOT included. They are not jurisdictional, and the
 * matter's own documents reach the session through the `matter_uuid` link
 * rather than through the source scope.
 */
export function sourcesForJurisdiction(
  catalogue: ResearchSource[],
  code: string | null
): ResearchSource[] {
  if (!code) return [];
  const wanted = code.toUpperCase();
  return (catalogue || []).filter(
    (source) => String(source?.jurisdiction || '').toUpperCase() === wanted
  );
}

export interface MatterResearchScope {
  jurisdiction: string | null;
  outputLanguage: string;
}

/** Both facts, for a caller that wants them together. */
export function matterResearchScope(matterData: any): MatterResearchScope {
  return {
    jurisdiction: matterJurisdictionCode(matterData),
    outputLanguage: matterOutputLanguage(matterData),
  };
}
